import { useEffect, useRef } from 'react'

/**
 * Fond animé du parcours d'authentification — réseau de points reliés.
 *
 * PORTAGE DIRECT de /opt/main-courante/server/public/login-particles.js
 * (le login de la Main Courante) : même logique de déplacement (rebond aux
 * bords), mêmes seuils de liaison (130 px entre points, 170 px vers le
 * curseur), même formule d'atténuation (alpha ∝ 1 − distance/seuil), même
 * densité (surface/16000, bornée 20–90), même gestion du devicePixelRatio,
 * même comportement tactile, même repli reduced-motion (points fixes, pas
 * de boucle). Ce qui change pour Gestion :
 *   · le rouge Main Courante (255,45,70) devient la palette LIVE CONTROL —
 *     points majoritairement violets, quelques cyan et bleus ; les fils vers
 *     la souris sont cyan (la couleur « flux de données » du design system) ;
 *   · les opacités sont légèrement adoucies (0.28/0.42/0.60 contre
 *     0.35/0.50/0.70) — plus premium, moins « radar » ;
 *   · le tout est un composant React : listeners retirés et rAF annulé au
 *     démontage (la Main Courante est une page qui meurt avec le script,
 *     une SPA n'a pas ce luxe) ;
 *   · `data-force-reduced="1"` (le réglage interne de Gestion) est traité
 *     comme prefers-reduced-motion.
 *
 * Usage STRICTEMENT limité au parcours d'authentification — le reste du
 * produit garde son fond calme (règle du design system).
 */

// Palette Gestion (RGB des tokens) : violet dominant, accents cyan et bleu.
const DOT_COLORS = [
  '124, 92, 255', // --violet  (~70 %)
  '124, 92, 255',
  '124, 92, 255',
  '34, 211, 238', // --cyan
  '59, 130, 246', // --blue
] as const
const MOUSE_COLOR = '34, 211, 238' // cyan — la couleur du flux

// Seuils et densité : identiques à la Main Courante.
const LINK_DISTANCE = 130
const MOUSE_LINK_DISTANCE = 170
const DOT_RADIUS = 1.8
const SPEED = 0.18

// Opacités adoucies pour Gestion (Main Courante : 0.35 / 0.5 / 0.7).
const LINK_ALPHA = 0.28
const MOUSE_ALPHA = 0.42
const DOT_ALPHA = 0.6

interface Particle { x: number; y: number; vx: number; vy: number; color: string }

function particleCountFor(w: number, h: number): number {
  return Math.max(20, Math.min(90, Math.round((w * h) / 16000)))
}

export function AuthAnimatedBackground(props: { dim?: boolean | undefined }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    const forceReduced = () => document.documentElement.getAttribute('data-force-reduced') === '1'
    let reduceMotion = media.matches || forceReduced()

    let width = 0
    let height = 0
    let particles: Particle[] = []
    const mouse = { x: -9999, y: -9999, active: false }
    let rafId: number | null = null

    function spawnParticle(): Particle {
      return {
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * SPEED * 2,
        vy: (Math.random() - 0.5) * SPEED * 2,
        color: DOT_COLORS[Math.floor(Math.random() * DOT_COLORS.length)]!,
      }
    }

    function resize() {
      const ratio = window.devicePixelRatio || 1
      width = window.innerWidth
      height = window.innerHeight
      canvas!.width = width * ratio
      canvas!.height = height * ratio
      canvas!.style.width = `${width}px`
      canvas!.style.height = `${height}px`
      ctx!.setTransform(1, 0, 0, 1, 0, 0)
      ctx!.scale(ratio, ratio)

      // Densité proportionnelle à la surface, bornée — pas d'explosion sur
      // grand écran, pas de désert sur petit.
      const target = particleCountFor(width, height)
      if (particles.length < target) {
        while (particles.length < target) particles.push(spawnParticle())
      } else if (particles.length > target) {
        particles.length = target
      }
      // Redessin immédiat : sans lui, un redimensionnement pendant que
      // l'onglet est masqué (rAF suspendu par le navigateur) laisserait un
      // canvas vide ou étiré jusqu'au retour au premier plan.
      draw()
    }

    function step() {
      for (const p of particles) {
        p.x += p.vx
        p.y += p.vy
        if (p.x < 0 || p.x > width) p.vx *= -1
        if (p.y < 0 || p.y > height) p.vy *= -1
        p.x = Math.max(0, Math.min(width, p.x))
        p.y = Math.max(0, Math.min(height, p.y))
      }
    }

    function draw() {
      ctx!.clearRect(0, 0, width, height)

      // Fils entre points proches — la paire prend la couleur du premier point.
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const a = particles[i]!
          const b = particles[j]!
          const dx = a.x - b.x
          const dy = a.y - b.y
          const dist = Math.sqrt(dx * dx + dy * dy)
          if (dist < LINK_DISTANCE) {
            const alpha = (1 - dist / LINK_DISTANCE) * LINK_ALPHA
            ctx!.strokeStyle = `rgba(${a.color}, ${alpha})`
            ctx!.lineWidth = 1
            ctx!.beginPath()
            ctx!.moveTo(a.x, a.y)
            ctx!.lineTo(b.x, b.y)
            ctx!.stroke()
          }
        }
      }

      // Fils vers le curseur — cyan, comme le flux de données du dashboard.
      if (mouse.active) {
        for (const p of particles) {
          const dx = p.x - mouse.x
          const dy = p.y - mouse.y
          const dist = Math.sqrt(dx * dx + dy * dy)
          if (dist < MOUSE_LINK_DISTANCE) {
            const alpha = (1 - dist / MOUSE_LINK_DISTANCE) * MOUSE_ALPHA
            ctx!.strokeStyle = `rgba(${MOUSE_COLOR}, ${alpha})`
            ctx!.lineWidth = 1
            ctx!.beginPath()
            ctx!.moveTo(p.x, p.y)
            ctx!.lineTo(mouse.x, mouse.y)
            ctx!.stroke()
          }
        }
      }

      // Points.
      for (const p of particles) {
        ctx!.fillStyle = `rgba(${p.color}, ${DOT_ALPHA})`
        ctx!.beginPath()
        ctx!.arc(p.x, p.y, DOT_RADIUS, 0, Math.PI * 2)
        ctx!.fill()
      }
    }

    function loop() {
      if (!reduceMotion) step()
      draw()
      rafId = reduceMotion ? null : requestAnimationFrame(loop)
    }

    const onMouseMove = (e: MouseEvent) => {
      mouse.x = e.clientX
      mouse.y = e.clientY
      mouse.active = true
    }
    const onMouseLeave = () => { mouse.active = false }
    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0]
      if (t) { mouse.x = t.clientX; mouse.y = t.clientY; mouse.active = true }
    }
    const onTouchEnd = () => { mouse.active = false }
    const onMotionChange = () => {
      // Bascule à chaud : on repart proprement dans le bon mode.
      reduceMotion = media.matches || forceReduced()
      if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null }
      mouse.active = false
      loop()
    }

    window.addEventListener('resize', resize)
    if (!reduceMotion) {
      // Sous reduced-motion, pas d'interaction souris : le fond est une image.
      window.addEventListener('mousemove', onMouseMove)
      window.addEventListener('mouseleave', onMouseLeave)
      window.addEventListener('touchmove', onTouchMove, { passive: true })
      window.addEventListener('touchend', onTouchEnd)
    }
    media.addEventListener('change', onMotionChange)

    resize()
    loop()

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      window.removeEventListener('resize', resize)
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseleave', onMouseLeave)
      window.removeEventListener('touchmove', onTouchMove)
      window.removeEventListener('touchend', onTouchEnd)
      media.removeEventListener('change', onMotionChange)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="lc-authbg"
      style={props.dim ? { opacity: 0.5 } : undefined}
      aria-hidden="true"
    />
  )
}
